import React, { Suspense, useState, useRef, useEffect, useId } from 'react'
import type { Monaco } from '@monaco-editor/react'
import type { editor, IPosition } from 'monaco-editor'
import s from '@/features/policies/components/RegoEditor.module.css'
import { PolicyInputHelp } from '@/features/policies/components/PolicyInputHelp'
import type { PolicyArtefactType } from '@/lib/apiTypes'
import { usePolicyInputSchema } from '@/lib/hooks'
import {
  aliasMap, chainAt, completionTarget, describePath, fieldSuggestions, resolveChain,
  type SchemaDoc,
} from '@/features/policies/lib/regoSchema'
import {
  REGO_LANGUAGE_COMPLETIONS, namespaceCompletions, namespaceTarget, type RegoCompletion,
} from '@/features/policies/lib/regoLanguage'
import { validateRego, type RegoSyntaxError } from '@/features/policies/lib/regoSyntax'
import { TEMPLATES, type Template } from '@/features/policies/lib/regoTemplates'
import { TemplateGallery } from '@/features/policies/components/TemplateGallery'
import { useTheme } from '@/context/ThemeContext'

// The catalogue lives in its own module (the gallery needs it without importing this one, and
// ~50 Rego bodies do not belong beside a Monaco wrapper). Re-exported so importers of the editor
// keep working.
export { TEMPLATES, type Template }

const MonacoEditor = React.lazy(() =>
  import('@monaco-editor/react').then((m) => ({ default: m.default }))
)

// ── Language + theme registration ─────────────────────────────────────────────

/**
 * Schemas the editor has loaded, keyed by artefact. Monaco's providers are registered
 * globally (once per Monaco instance) rather than per editor, so they read the artefact
 * off the model's path - `modelPath()` below is what makes that lookup possible.
 */
const SCHEMAS = new Map<PolicyArtefactType, SchemaDoc>()

/** `policy-<artefact>-<instance>.rego`: the artefact is what the providers read back, the
 *  instance id keeps two editors on screen at once from sharing one Monaco model. */
const modelPath = (artefactType: PolicyArtefactType, uid: string) => `policy-${artefactType}-${uid}.rego`

function schemaForModel(model: { uri: { path: string } }): SchemaDoc | null {
  const kind = /policy-([a-z]+)-[A-Za-z0-9]*\.rego$/.exec(model.uri.path)?.[1] as PolicyArtefactType | undefined
  return kind ? SCHEMAS.get(kind) ?? null : null
}

type Range = { startLineNumber: number; endLineNumber: number; startColumn: number; endColumn: number }

/** Turn this repo's language data (keywords, skeleton, builtins) into Monaco suggestions. */
function languageSuggestions(monaco: Monaco, items: RegoCompletion[], range: Range) {
  const KIND = {
    keyword:  monaco.languages.CompletionItemKind.Keyword,
    function: monaco.languages.CompletionItemKind.Function,
    snippet:  monaco.languages.CompletionItemKind.Snippet,
    variable: monaco.languages.CompletionItemKind.Variable,
  }
  return items.map((item) => ({
    label: item.label,
    kind:  KIND[item.flavour],
    insertText: item.insertText,
    ...(item.snippet ? { insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet } : {}),
    ...(item.detail ? { detail: item.detail } : {}),
    ...(item.documentation ? { documentation: { value: item.documentation } } : {}),
    ...(item.filterText ? { filterText: item.filterText } : {}),
    sortText: `${item.group}${item.label}`,
    range,
  }))
}

/** True when the cursor sits just after a member access, i.e. the user wants fields, not snippets. */
const MEMBER_CONTEXT = /[A-Za-z0-9_\]]\.\w*$/

function registerRego(monaco: Monaco) {
  if (monaco.languages.getLanguages().some((l: { id: string }) => l.id === 'rego')) return

  monaco.languages.register({ id: 'rego' })

  // Editor mechanics VS Code gives every language: comment toggling (cmd+/), bracket
  // matching, auto-closing quotes and braces, and indent after `{`.
  monaco.languages.setLanguageConfiguration('rego', {
    comments:          { lineComment: '#' },
    brackets:          [['{', '}'], ['[', ']'], ['(', ')']],
    autoClosingPairs:  [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"', notIn: ['string'] },
      { open: '`', close: '`', notIn: ['string'] },
    ],
    surroundingPairs:  [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
    ],
    indentationRules: {
      increaseIndentPattern: /\{[^}]*$/,
      decreaseIndentPattern: /^\s*\}/,
    },
  })

  // Completions for `input.*` (and locals bound to it) come from the served policy-input
  // JSON Schema, so every field offered is one the scanners actually populate.
  monaco.languages.registerCompletionItemProvider('rego', {
    triggerCharacters: ['.'],
    provideCompletionItems(model: editor.ITextModel, position: IPosition) {
      const word = model.getWordUntilPosition(position)
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber:   position.lineNumber,
        startColumn:     word.startColumn,
        endColumn:       position.column,
      }
      const line = model.getValueInRange({
        startLineNumber: position.lineNumber,
        startColumn:     1,
        endLineNumber:   position.lineNumber,
        endColumn:       position.column,
      })
      // 1. An `input.*` (or alias) path: answer from the artefact's schema.
      const doc = schemaForModel(model)
      const target = doc ? completionTarget(line, aliasMap(model.getValue())) : null
      if (!doc || !target) {
        // 2. Some other member access. The only other dotted names in Rego are namespaced
        // builtins (`regex.`, `time.`, `semver.`); anything else gets silence rather than
        // keywords sprayed into the middle of a reference.
        if (MEMBER_CONTEXT.test(line)) {
          const namespace = namespaceTarget(line)
          return { suggestions: languageSuggestions(monaco, namespace ? namespaceCompletions(namespace) : [], range) }
        }
        // 3. Open code: the whole language - skeleton, keywords, builtins.
        return { suggestions: languageSuggestions(monaco, REGO_LANGUAGE_COMPLETIONS, range) }
      }
      return {
        suggestions: fieldSuggestions(doc, target.path).map((field) => ({
          label:         field.label,
          kind:          monaco.languages.CompletionItemKind.Field,
          insertText:    field.insertText,
          detail:        field.type,
          documentation: {
            value: `\`${field.path}\`${field.required ? ' *(always present)*' : ''}${field.description ? `\n\n${field.description}` : ''}`,
          },
          // Required fields first, then alphabetical; an array's `[_]` variant sorts next to it.
          sortText: `${field.required ? '0' : '1'}${field.label}`,
          range,
        })),
      }
    },
  })

  // Hovering any `input.*` reference explains it from the same contract.
  monaco.languages.registerHoverProvider('rego', {
    provideHover(model: editor.ITextModel, position: IPosition) {
      const doc = schemaForModel(model)
      if (!doc) return null
      const chain = chainAt(model.getLineContent(position.lineNumber), position.column)
      const path = chain ? resolveChain(chain, aliasMap(model.getValue())) : null
      const hit = path ? describePath(doc, path) : null
      if (!hit) return null
      return {
        contents: [
          { value: `\`\`\`rego\n${hit.path}\n\`\`\`` },
          { value: `**${hit.type}**${hit.description ? `\n\n${hit.description}` : ''}` },
        ],
      }
    },
  })

  monaco.languages.setMonarchTokensProvider('rego', {
    keywords: ['package', 'import', 'default', 'not', 'with', 'as', 'else', 'some', 'every', 'in', 'contains', 'if'],
    tokenizer: {
      root: [
        [/#.*$/, 'comment'],
        [/"(?:[^"\\]|\\.)*"/, 'string'],
        [/`[^`]*`/, 'string'],
        [/\b(?:true|false|null)\b/, 'keyword'],
        [/\b\d+(?:\.\d+)?\b/, 'number'],
        [/\b(?:package|import|default|not|with|as|else|some|every|in|contains|if)\b/, 'keyword'],
        [/\b(?:deny|allow|violation|warn|input|data)\b/, 'type'],
        [/[a-zA-Z_]\w*/, 'identifier'],
        [/[{}[\]()]/, 'delimiter.bracket'],
        [/:=|==|!=|>=|<=|>|<|\||&/, 'operator'],
        [/[;,.]/, 'delimiter'],
      ],
    },
  })

  monaco.editor.defineTheme('terramantle-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'keyword',           foreground: 'a35bf5' },
      { token: 'type',              foreground: 'dde6f0' },
      { token: 'comment',           foreground: '70849b', fontStyle: 'italic' },
      { token: 'string',            foreground: '22c55e' },
      { token: 'number',            foreground: '3b82f6' },
      { token: 'operator',          foreground: '6b7f96' },
      { token: 'identifier',        foreground: 'dde6f0' },
      { token: 'delimiter.bracket', foreground: '6b7f96' },
    ],
    colors: {
      'editor.background':              '#0b0e14',
      'editor.foreground':              '#dde6f0',
      'editor.lineHighlightBackground': '#0f1219',
      'editorLineNumber.foreground':    '#70849b',
      'editorLineNumber.activeForeground': '#8497ad',
      'editorCursor.foreground':        '#a35bf5',
      'editor.selectionBackground':     '#8c25ea33',
      'editorIndentGuide.background1':  '#131720',
      // Suggest/hover popups: same surface + accent-tinted border as the app's panels,
      // so schema completions read as part of Terramantle rather than stock VS Code.
      'editorWidget.background':                    '#0f1219',
      'editorWidget.border':                        '#8c25ea1a',
      'editorSuggestWidget.background':             '#0f1219',
      'editorSuggestWidget.border':                 '#8c25ea1a',
      'editorSuggestWidget.foreground':             '#dde6f0',
      'editorSuggestWidget.selectedBackground':     '#8c25ea33',
      'editorSuggestWidget.selectedForeground':     '#ffffff',
      'editorSuggestWidget.highlightForeground':    '#a35bf5',
      'editorSuggestWidget.focusHighlightForeground': '#a35bf5',
      'editorHoverWidget.background':               '#0f1219',
      'editorHoverWidget.border':                   '#8c25ea1a',
      'editorHoverWidget.foreground':               '#dde6f0',
      // Syntax errors underline in the app's red, matching the box border and the bar below it.
      'editorError.foreground':                     '#ef4444',
    },
  })

  // Light counterpart - same token roles, stepped for a light surface (all text >= 4.5:1 on #f5f7fa).
  monaco.editor.defineTheme('terramantle-light', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: 'keyword',           foreground: '8c25ea' },
      { token: 'type',              foreground: '111827' },
      { token: 'comment',           foreground: '5b6270', fontStyle: 'italic' },
      { token: 'string',            foreground: '15803d' },
      { token: 'number',            foreground: '2563eb' },
      { token: 'operator',          foreground: '4b5563' },
      { token: 'identifier',        foreground: '111827' },
      { token: 'delimiter.bracket', foreground: '4b5563' },
    ],
    colors: {
      'editor.background':              '#f5f7fa',
      'editor.foreground':              '#111827',
      'editor.lineHighlightBackground': '#edf0f5',
      'editorLineNumber.foreground':    '#5b6270',
      'editorLineNumber.activeForeground': '#374151',
      'editorCursor.foreground':        '#8c25ea',
      'editor.selectionBackground':     '#8c25ea22',
      'editorIndentGuide.background1':  '#e4e8ef',
      'editorWidget.background':                    '#ffffff',
      'editorWidget.border':                        '#8c25ea26',
      'editorSuggestWidget.background':             '#ffffff',
      'editorSuggestWidget.border':                 '#8c25ea26',
      'editorSuggestWidget.foreground':             '#111827',
      'editorSuggestWidget.selectedBackground':     '#8c25ea22',
      'editorSuggestWidget.selectedForeground':     '#111827',
      'editorSuggestWidget.highlightForeground':    '#8c25ea',
      'editorSuggestWidget.focusHighlightForeground': '#8c25ea',
      'editorHoverWidget.background':               '#ffffff',
      'editorHoverWidget.border':                   '#8c25ea26',
      'editorHoverWidget.foreground':               '#111827',
      'editorError.foreground':                     '#dc2626',
    },
  })
}

// ── Editor ────────────────────────────────────────────────────────────────────

interface RegoEditorProps {
  value: string
  onChange: (value: string) => void
  /**
   * Editor height, as a CSS length. Defaults to a fixed 320px box; pass `"100%"` (see the
   * field wrapper's `fill`) to have it take whatever height its container gives it instead.
   * The height covers the whole box, so the diagnostics bar grows into it rather than
   * shoving the form around as the author types.
   */
  height?: number | string
  /**
   * Called whenever the syntax check's verdict changes, so the form owning this editor can
   * refuse to save Rego that will not parse. Keep it referentially stable (a `useState`
   * setter does).
   */
  onErrorsChange?: (errors: RegoSyntaxError[]) => void
}

/** Marker owner, so the editor only ever clears markers it put there itself. */
const SYNTAX_MARKERS = 'rego-syntax'

/** Monaco's editor handle, derived from the `Monaco` namespace type rather than imported:
 *  the workspace resolves two copies of monaco-editor, and only the one @monaco-editor/react
 *  itself loaded is assignable from what `onMount` hands over. */
type CodeEditor = ReturnType<Monaco['editor']['create']>

/** How long the author gets to finish a thought before the box judges it. */
const VALIDATE_DEBOUNCE_MS = 400

export function RegoEditor({ value, onChange, artefactType = 'module', height = 320, onErrorsChange }: RegoEditorProps & { artefactType?: PolicyArtefactType }) {
  const { theme } = useTheme()
  // Same endpoint the (?) helper reads. Completions and hovers are driven by whatever it
  // returns, so the contract is defined in exactly one place (the worker's schema module).
  const { data: schema } = usePolicyInputSchema(artefactType)
  useEffect(() => {
    if (schema) SCHEMAS.set(artefactType, schema)
  }, [artefactType, schema])
  const uid = useId().replace(/[^A-Za-z0-9]/g, '')

  const monacoRef = useRef<Monaco | null>(null)
  const editorRef = useRef<CodeEditor | null>(null)
  const [mounted, setMounted] = useState(false)
  const [errors, setErrors]   = useState<RegoSyntaxError[]>([])

  // Debounced, because half-typed Rego is nearly always invalid Rego: an opened brace, an
  // unclosed quote, a rule head with no body yet. A border that flashes red at every
  // keystroke is a border the author learns to ignore.
  useEffect(() => {
    const timer = setTimeout(() => setErrors(validateRego(value)), VALIDATE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [value])

  useEffect(() => { onErrorsChange?.(errors) }, [errors, onErrorsChange])

  // The same findings, as markers: red squiggle on the offending span plus the message on
  // hover, which is where an author looks once the bar has told them which line to look at.
  useEffect(() => {
    const monaco = monacoRef.current
    const model  = editorRef.current?.getModel()
    if (!monaco || !model) return
    monaco.editor.setModelMarkers(model, SYNTAX_MARKERS, errors.map((e) => ({
      severity:        monaco.MarkerSeverity.Error,
      message:         e.message,
      startLineNumber: e.line,
      startColumn:     e.column,
      endLineNumber:   e.line,
      endColumn:       e.endColumn,
    })))
  }, [errors, mounted])

  // Models are cached by path across mounts, so markers outlive this component unless they
  // are cleared - a closed modal would otherwise reopen already squiggled.
  useEffect(() => () => {
    const model = editorRef.current?.getModel()
    if (monacoRef.current && model) monacoRef.current.editor.setModelMarkers(model, SYNTAX_MARKERS, [])
  }, [])

  /** Clicking a finding puts the caret on it - the bar is a way back to the line, not just a label. */
  function reveal(e: RegoSyntaxError) {
    const ed = editorRef.current
    if (!ed) return
    ed.revealLineInCenter(e.line)
    ed.setPosition({ lineNumber: e.line, column: e.column })
    ed.focus()
  }

  const invalid = errors.length > 0

  return (
    <div className={`${s.editorBox} ${invalid ? s.editorBoxInvalid : ''}`} style={{ height }}>
      <div className={s.editorHost}>
        <Suspense fallback={<div className={s.editorFallback} />}>
          <MonacoEditor
            height="100%"
            language="rego"
            // The model path is how the globally-registered providers know which artefact's
            // schema to complete against.
            path={modelPath(artefactType, uid)}
            theme={theme === 'light' ? 'terramantle-light' : 'terramantle-dark'}
            value={value}
            onChange={(v) => onChange(v ?? '')}
            beforeMount={registerRego}
            onMount={(ed, monaco) => {
              editorRef.current = ed
              monacoRef.current = monaco
              setMounted(true)
            }}
            options={{
              minimap:              { enabled: false },
              wordWrap:             'on',
              // The height can be viewport-relative, so Monaco has to re-measure itself
              // on resize rather than keeping the dimensions it was mounted with.
              automaticLayout:      true,
              fontFamily:           "'JetBrains Mono', 'Fira Code', monospace",
              fontSize:             12.5,
              lineNumbers:          'on',
              scrollBeyondLastLine: false,
              renderLineHighlight:  'all',
              padding:              { top: 12, bottom: 12 },
              overviewRulerLanes:   0,
              scrollbar:            { verticalScrollbarSize: 6, horizontalScrollbarSize: 6 },
              renderWhitespace:     'none',
              // Schema completions should appear as you type, not only on ctrl+space.
              quickSuggestions:     { other: true, comments: false, strings: false },
              // The box clips (rounded corners), and so does the modal panel around it.
              // Position the suggest/hover popups fixed so neither one cuts them off.
              fixedOverflowWidgets: true,
            }}
          />
        </Suspense>
      </div>
      {invalid && (
        <div className={s.syntaxBar} role="alert">
          <div className={s.syntaxBarHead}>
            <span className={s.syntaxIcon} aria-hidden="true">!</span>
            {errors.length === 1 ? 'Syntax error' : `${errors.length} syntax errors`}
            <span className={s.syntaxBarNote}>this policy will not parse</span>
          </div>
          <ul className={s.syntaxList}>
            {errors.map((e, i) => (
              <li key={`${e.line}:${e.column}:${i}`}>
                <button type="button" className={s.syntaxRow} onClick={() => reveal(e)}>
                  <span className={s.syntaxLineNo}>Line {e.line}</span>
                  <span className={s.syntaxMsg}>{e.message}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ── Field wrapper (label row + template picker + editor) ──────────────────────

/**
 * `fill`: the field claims the leftover height of a flex-column parent and hands all of it to
 * the editor (the policy modal, which is built around the editor). Without it the field is
 * intrinsically sized and the editor keeps its fixed height.
 */
export function RegoEditorField({ value, onChange, artefactType = 'module', height, fill = false, onErrorsChange, onTemplatePick }: RegoEditorProps & {
  artefactType?: PolicyArtefactType
  fill?: boolean
  /**
   * Picked a template in the gallery. Gets the whole template rather than just its Rego, so the
   * form that owns this field can also fill in a policy name and description. The Rego itself
   * still arrives through `onChange`, so the buffer has exactly one owner either way.
   */
  onTemplatePick?: (t: Template) => void
}) {
  const [gallery, setGallery]   = useState(false)
  const [showHelp, setShowHelp] = useState(false)

  const templates = TEMPLATES[artefactType] ?? TEMPLATES.module

  return (
    <div className={fill ? s.fieldFill : undefined}>
      {showHelp && <PolicyInputHelp artefactType={artefactType} onClose={() => setShowHelp(false)} />}
      {gallery && (
        <TemplateGallery
          artefactType={artefactType}
          onPick={(t) => { onChange(t.rego); onTemplatePick?.(t) }}
          onClose={() => setGallery(false)}
        />
      )}
      {/* Label row */}
      <div className={s.labelRow}>
        <label className={`field-label ${s.labelFieldLabel}`}>Rego policy</label>
        <span className={s.regoBadge}>REGO</span>
        <span className={s.labelHint}>
          must define <code style={{ color: 'var(--accent)' }}>data.terramantle.policy.deny (deny contains msg)</code>
        </span>
        <button
          type="button"
          className={s.helpBtn}
          aria-label="Show the input schema this policy receives"
          title="What data can this policy read?"
          onClick={() => setShowHelp(true)}
        >
          ?
        </button>
        <div className={s.spacer} />
        {/* Template picker. Guarded on the set being non-empty: every artefact ships templates
            today, but the field should not offer a gallery with nothing in it. */}
        {templates.length > 0 && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ fontSize: '0.72rem', gap: 4 }}
            onClick={() => setGallery(true)}
          >
            Browse templates
          </button>
        )}
      </div>

      {fill ? (
        <div className={s.editorFill}>
          <RegoEditor value={value} onChange={onChange} artefactType={artefactType} height="100%" onErrorsChange={onErrorsChange} />
        </div>
      ) : (
        <RegoEditor value={value} onChange={onChange} artefactType={artefactType} height={height} onErrorsChange={onErrorsChange} />
      )}
    </div>
  )
}
